import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import type { SettingsWorkspaceSettings } from '@customerio/hydra-ui-api';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { hydraUiApi } from '@/services/api';
import { environmentKeys, parseEnvironment, type WorkspaceEnvironment } from './entity';

const STALE_TIME = 60_000;

function useValidWorkspaceId(): number | null {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  return workspaceId != null && workspaceId > 0 ? workspaceId : null;
}

/** @public */
export async function getEnvironment(workspaceId: number, signal?: AbortSignal): Promise<WorkspaceEnvironment> {
  const raw = await hydraUiApi.environments.get({
    params: { path: { environment_id: String(workspaceId) } },
    signal,
  });
  return parseEnvironment(raw);
}

/** @public */
export const getEnvironmentQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: environmentKeys.current(workspaceId),
    queryFn: () => getEnvironment(workspaceId),
    staleTime: STALE_TIME,
  });

/**
 * @public The workspace's enabled, non-CDP tracking site ids, in the order
 * services auto-selects from. Unlike the API credentials list, disabled
 * credentials are left out.
 */
export async function getSiteIds(workspaceId: number): Promise<string[]> {
  const data = await hydraUiApi.environments.getSiteIds({
    params: { path: { environment_id: String(workspaceId) } },
  });
  return data.site_ids;
}

/** `staleTime: 0`: credentials are edited on the API credentials page, so a revisit refetches. */
export const getSiteIdsQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: environmentKeys.siteIds(workspaceId),
    queryFn: () => getSiteIds(workspaceId),
    staleTime: 0,
  });

export const workspaceSettingsKeys = {
  detail: (workspaceId: number) => ['workspace-settings', workspaceId] as const,
};

/** @public */
export async function getWorkspaceSettings(workspaceId: number, signal?: AbortSignal) {
  return hydraUiApi.workspaceSettings.getSettingsWorkspace({
    params: { path: { environment_id: String(workspaceId) } },
    signal,
  });
}

export type UpdateWorkspaceSettingsInput<
  TSettings extends Partial<SettingsWorkspaceSettings> = Partial<SettingsWorkspaceSettings>,
> = {
  workspaceId: number;
  settings: TSettings;
};

/** @public */
export async function updateWorkspaceSettings<TSettings extends Partial<SettingsWorkspaceSettings>>(
  input: UpdateWorkspaceSettingsInput<TSettings>,
) {
  return hydraUiApi.workspaceSettings.updateSettingsWorkspace({
    params: { path: { environment_id: String(input.workspaceId) } },
    body: { settings: input.settings },
  });
}

/**
 * Non-suspense environment read for the current workspace. Reads the
 * workspace id from the URL and routes to `skipToken` until it resolves —
 * the same "optional" pattern as `useDomainsOptional`, so callers that may
 * render before the workspace is in scope don't register a `(0)` slot.
 *
 * Pass `workspaceIdOverride` (e.g. `useCurrentWorkspace()?.id`) for callers that
 * render on non-numeric routes like `/workspaces/last/...`, where the URL has no
 * numeric workspace segment but the current workspace is still resolved.
 *
 * @public
 */
export function useEnvironmentOptional(workspaceIdOverride?: number) {
  const urlWorkspaceId = useValidWorkspaceId();
  const validWorkspaceId =
    workspaceIdOverride != null && workspaceIdOverride > 0 ? workspaceIdOverride : urlWorkspaceId;
  return useQuery({
    queryKey: validWorkspaceId != null ? environmentKeys.current(validWorkspaceId) : ['environment', 'no-workspace'],
    queryFn: validWorkspaceId != null ? () => getEnvironment(validWorkspaceId) : skipToken,
    staleTime: STALE_TIME,
  });
}
